import type { Pills as PillsType } from '@seek/chalice-types';
import {
  Box,
  IconAdd,
  IconClear,
  Text,
  useResponsiveValue,
} from 'braid-design-system';
import { forwardRef } from 'react';

import type { JobSearchAssistPill } from 'src/graphql/graphql.ts';
import { truncateText } from 'src/utils/truncateText/truncateText.ts';

import * as styles from './Pills.css.ts';

import * as refineBarStyles from '../../../RefineBarV2/RefineBarV2.css';

export type OnPillSelectProps = {
  isActive: boolean;
  keywords: string;
  label: string;
  position: number;
  stateToken?: string;
};

interface PillsProps {
  handleOnPillClick: (pill: OnPillSelectProps) => void;
  pills: PillsType | JobSearchAssistPill[];
}

export const Pills = forwardRef<HTMLElement, PillsProps>(
  ({ handleOnPillClick, pills }, ref) => {
    const isMobile =
      useResponsiveValue()({
        mobile: true,
        tablet: false,
      }) || false;

    return (
      <Box display="flex" ref={ref}>
        {pills?.map((pill, index) => {
          const { label, keywords, isActive } = pill;
          // stateToken may not exist in PillsType items
          const stateToken = 'stateToken' in pill ? pill.stateToken : undefined;

          return (
            <Box
              data={{ automation: `pill-${index}` }}
              key={index}
              className="dynamicPill"
              display="flex"
              paddingRight={pills.length - 1 === index ? 'none' : 'xxsmall'}
            >
              <Box
                background={isActive ? 'customLight' : 'customDark'}
                data-selected={isActive ? 'true' : 'false'}
                className={refineBarStyles.pillButton}
                onClick={() =>
                  handleOnPillClick({
                    isActive,
                    keywords,
                    label,
                    position: index,
                    stateToken,
                  })
                }
              >
                <Box className={styles.innerTextPill}>
                  <Text
                    baseline={false}
                    maxLines={1}
                    size={isMobile ? 'small' : undefined}
                  >
                    {isMobile
                      ? truncateText({ text: label, length: 35 })
                      : label}
                  </Text>
                  <Box
                    className={{
                      [styles.pillIcon]: true,
                      [refineBarStyles.pillIcon]: true,
                    }}
                  >
                    <Text
                      baseline={false}
                      size="small"
                      icon={isActive ? <IconClear /> : <IconAdd />}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  },
);
